import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { SyncDashboardCard } from '../../components/admin/SyncDashboardCard';
import type { SyncDashboardEntry } from '../../types/admin';
import { useSyncStore, type SyncRun } from '../../store/syncStore';
import SyncRunDetailModal from '../../components/syncs/SyncRunDetailModal';

export function SyncJobsTab() {
  const [entries, setEntries] = useState<SyncDashboardEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingTrigger, setPendingTrigger] = useState<string | null>(null);
  const [openRun, setOpenRun] = useState<SyncRun | null>(null);

  const { runs, fetch: fetchRuns, getDetail } = useSyncStore();

  async function loadDashboard() {
    try {
      const res = await api.get<{ syncs: SyncDashboardEntry[] }>('/api/admin/syncs/dashboard');
      setEntries(res.data.syncs);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }

  useEffect(() => {
    loadDashboard();
    fetchRuns({ limit: 20 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleTrigger(syncName: string) {
    setPendingTrigger(syncName);
    try {
      await api.post('/api/admin/syncs/trigger', { syncName });
      await loadDashboard();
      await fetchRuns({ limit: 20 });
    } finally {
      setPendingTrigger(null);
    }
  }

  async function handleRowClick(id: string) {
    const detail = await getDetail(id);
    if (detail) setOpenRun(detail);
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">
        <div>{error}</div>
        <button className="mt-2 text-xs underline" onClick={loadDashboard}>Retry</button>
      </div>
    );
  }

  if (!entries) return <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>;

  const inbound = entries.filter((e) => e.direction === 'inbound');
  const outbound = entries.filter((e) => e.direction === 'outbound');

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h2 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Inbound (Blast → CRM)</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {inbound.map((e) => (
            <SyncDashboardCard
              key={e.syncName}
              entry={e}
              onTrigger={handleTrigger}
              triggerPending={pendingTrigger === e.syncName}
            />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Outbound (CRM → Data Mart)</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {outbound.map((e) => (
            <SyncDashboardCard
              key={e.syncName}
              entry={e}
              onTrigger={handleTrigger}
              triggerPending={pendingTrigger === e.syncName}
            />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Recent runs</h2>
        <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2 text-left">Sync</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Started</th>
                <th className="px-3 py-2">Duration</th>
                <th />
              </tr>
            </thead>
            <tbody className="text-gray-900 dark:text-gray-100">
              {runs.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">No runs yet</td></tr>
              ) : runs.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => handleRowClick(r.id)}>
                  <td className="px-3 py-2">{r.syncName}</td>
                  <td className="px-3 py-2 text-center">{r.status}</td>
                  <td className="px-3 py-2 text-center">{r.startedAt ? new Date(r.startedAt).toLocaleString() : '—'}</td>
                  <td className="px-3 py-2 text-center">{r.durationMs ? `${Math.round(r.durationMs/1000)}s` : '—'}</td>
                  <td className="px-3 py-2 text-right text-xs text-blue-600 dark:text-blue-400">Details →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {openRun && <SyncRunDetailModal run={openRun} onClose={() => setOpenRun(null)} />}
    </div>
  );
}
